import { api } from './api';

export type AdminNotification = {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  isRead: boolean;
};

export async function getAdminNotifications(unreadOnly = false) {
  const query = unreadOnly ? '?unread_only=true' : '';
  const payload = await api.get<unknown>(`/admin/notifications${query}`);
  return normalizeNotificationList(payload);
}

export async function getAdminNotificationUnreadCount() {
  const payload = await api.get<unknown>('/admin/notifications/unread-count');
  return normalizeUnreadCount(payload);
}

export function markAdminNotificationRead(notificationId: string) {
  return api.patch(`/admin/notifications/${notificationId}/read`);
}

export function markAllAdminNotificationsRead() {
  return api.patch('/admin/notifications/read-all');
}

export function normalizeNotification(payload: unknown): AdminNotification | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as Record<string, unknown>;
  const message = readString(source, 'message')
    ?? readString(source, 'body')
    ?? readString(source, 'detail')
    ?? readString(source, 'event');

  if (!message) {
    return null;
  }

  return {
    id: readString(source, 'id') ?? readString(source, 'notification_id') ?? `${Date.now()}-${Math.random()}`,
    title: readString(source, 'title') ?? readString(source, 'type') ?? 'Admin notification',
    message,
    createdAt: readString(source, 'created_at') ?? readString(source, 'timestamp') ?? new Date().toISOString(),
    isRead: readBoolean(source, 'is_read') ?? readBoolean(source, 'read') ?? false,
  };
}

function normalizeNotificationList(payload: unknown) {
  if (Array.isArray(payload)) {
    return payload.map(normalizeNotification).filter(isNotification);
  }

  if (payload && typeof payload === 'object') {
    const source = payload as Record<string, unknown>;
    const list = source.notifications ?? source.items ?? source.data;

    if (Array.isArray(list)) {
      return list.map(normalizeNotification).filter(isNotification);
    }
  }

  return [];
}

function normalizeUnreadCount(payload: unknown) {
  if (typeof payload === 'number') {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    const source = payload as Record<string, unknown>;
    const count = source.unread_count ?? source.unread_total ?? source.count;
    return typeof count === 'number' ? count : 0;
  }

  return 0;
}

function isNotification(notification: AdminNotification | null): notification is AdminNotification {
  return notification !== null;
}

function readString(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === 'string' && value ? value : undefined;
}

function readBoolean(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return typeof value === 'boolean' ? value : undefined;
}
